// FNP-79: create / update mutations include the FNP-76 programmatic-
// provisioning input fields. The schema accepts them as optional, so the
// legacy admin-create flow that omits them continues to work.

const APPLICATION_RESPONSE_FIELDS = `
    id
    name
    description
    url
    logoUrl
    applicationCategory
    applicationStatus
    displayName
    businessDomain
    applicationType
    parentDomainName
    subdomain
    deploymentAccountGroupId
    provisioningExecutionId
    stepFunctionsExecutionArn
    statusReason
    progress { currentStep completedSteps failedStep }
`;

export const createApplication = `
    mutation createApplication($input: CreateApplicationInput!) {
        createApplication(input:$input) {
            ${APPLICATION_RESPONSE_FIELDS}
    }
}`;

export const updateApplication = `
    mutation updateApplication($input: UpdateApplicationInput!) {
        updateApplication(input:$input){
            ${APPLICATION_RESPONSE_FIELDS}
        }
}`;

export const deleteApplication = `
    mutation deleteApplication($input: DeleteApplicationInput!) {
        deleteApplication(input:$input) {
            id
        }

}`;

// FNP-998: start a teardown.
//
// ⚠️ This does NOT delete the Application record — that is `deleteApplication`,
// above, which cascades the DynamoDB record graph and touches no AWS
// infrastructure. This is the opposite: it tears down the INFRASTRUCTURE and
// leaves the record, marked DECOMMISSIONED, so provenance survives.
//
// The mutation returns immediately with the workflows-api Execution driving the
// teardown; it does not wait for it. The console subscribes to that execution
// for live progress, exactly as the create flow does.
//
// ⚠️ `stage` is required rather than defaulted. A defaulted stage on a
// destructive operation is how the wrong environment gets deleted.
// FNP-1013: provisioning is its own VERB, and the admin chooses the playbook.
//
// Until this existed, creating an application and building its infrastructure
// were one mutation, and whether to build was INFERRED from whether an optional
// field happened to be populated — so filling in a dropdown silently created
// two repositories, two pipelines, a bucket, a CloudFront distribution and DNS.
//
// ⚠️ `playbookId` comes from the CALLER. That is the whole point: the playbook
// id used to be a placeholder substituted at `cdk synth` from per-stage config,
// because an APPSYNC_JS function has no environment variables. So a playbook an
// administrator created in a console needed a commit and a pipeline deploy, in
// every environment, before it could be used — roughly six deploys to wire one
// playbook. Passing the id kills that.
//
// ⚠️ `stage` is required rather than defaulted, for the same reason it is on
// deprovision: a defaulted stage on a consequential operation is how the wrong
// environment gets touched.
//
// Returns immediately with the workflows-api Execution driving the provision;
// it does not wait. The console reads live progress from that execution.
export const provisionApplication = `
    mutation provisionApplication($input: ProvisionApplicationInput!) {
        provisionApplication(input:$input) {
            applicationId
            executionId
            sfnExecutionArn
            executionStatus
        }
}`;

export const deprovisionApplication = `
    mutation deprovisionApplication($input: DeprovisionApplicationInput!) {
        deprovisionApplication(input:$input) {
            applicationId
            executionId
            sfnExecutionArn
            executionStatus
        }
}`;

// FNP-1007: mark an application decommissioned WITHOUT deleting anything.
//
// ⚠️ THIS IS NOT A LIGHTER `deprovisionApplication`. That one DESTROYS
// infrastructure. This one destroys NOTHING: it asks the server to prove the
// infrastructure is already gone and, only if the proof holds, corrects the
// record to say so. The blast radius is one DynamoDB row.
//
// It exists for records that are ACTIVE but own nothing — torn down by hand,
// record left behind. Six of them exist. `deprovisionApplication` cannot help
// them: it refuses without a stack name, and there is no stack name to add.
//
// ⚠️ NO `stage` IN THE INPUT, deliberately (see the schema's own note). The
// sweep is application-wide — stacks, buckets, repositories and pipelines
// across every stage — so a stage argument would imply a narrowing the server
// does not perform. Do not add one for symmetry with deprovision.
//
// The result is STRUCTURED ON REFUSAL rather than a bare error string, because
// the admin's next step differs completely between the two refusal modes:
//   `found`            — resources PROVED to still exist. Do not mark. The
//                        record is correct; use Decommission to tear down.
//   `incompleteChecks` — a sweep could not complete, so it proved NOTHING
//                        either way. Retry or escalate; a listing denied by IAM
//                        returns zero items and looks exactly like an empty
//                        account, which is why the server fails closed.
// Both are selected here so the UI can render them as distinct outcomes.
export const markApplicationDecommissioned = `
    mutation markApplicationDecommissioned($input: MarkApplicationDecommissionedInput!) {
        markApplicationDecommissioned(input:$input) {
            applicationId
            marked
            name
            originalName
            decommissionedAt
            decommissionMethod
            reason
            found {
                resourceType
                name
            }
            incompleteChecks {
                resourceType
                failureKind
                failureReason
            }
        }
}`;
